import {
  deriveAccountType,
  normalizeAccountTypeForApi,
} from "$lib/util/account"
import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import { ACCOUNT_TYPES } from "@tastyworks/shared-utils/misc/constants"
import {
  type AccountOpeningApplication,
  type AccountTypeChoiceValue,
  ACCOUNT_TYPE_CHOICES,
  accountTypeToChoiceMapping,
  BENEFICIARY_ACCOUNT_TYPES,
  ENTITY_ACCOUNT_TYPE_CHOICES,
  ENTITY_ACCOUNT_TYPES,
  IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES,
  IRA_INDIVIDUAL,
  IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES,
  JOINT_ACCOUNT_CHOICES,
  JOINT_ACCOUNT_TYPES,
  MARGIN_TYPES,
  RETIREMENT_ACCOUNT_CHOICES,
  RETIREMENT_ACCOUNT_TYPES,
  TAX_ELECTION_ORDERED,
} from "@tastyworks/tastyworks-api"
import { bool, type InferType, mixed, object, string } from "yup"

export const accountTypeSelectSchema = object({
  hasNonEntityAccount: bool().default(false).notRequired(),
  accountTypeName: mixed<ACCOUNT_TYPES>().oneOf(Object.values(ACCOUNT_TYPES)),
  accountType: mixed<ACCOUNT_TYPE_CHOICES>()
    .oneOf(Object.values(ACCOUNT_TYPE_CHOICES))
    .required()
    .test(
      "is-entity-account",
      "yup.isEntityPermitted.errorMessage",
      (value, context) => {
        if (
          value !== ACCOUNT_TYPE_CHOICES.ENTITY &&
          value !== ACCOUNT_TYPE_CHOICES.HEDGE_FUND
        ) {
          return true
        }
        return !context.parent.hasNonEntityAccount
      }
    ),
})

function accountTypePatch(
  accountType: AccountTypeChoiceValue | undefined
): Partial<AccountOpeningApplication> {
  const derivedAccountType = deriveAccountType(accountType)
  if (!derivedAccountType) {
    return {}
  }

  const accountTypeName = normalizeAccountTypeForApi(derivedAccountType)
  if (RETIREMENT_ACCOUNT_TYPES.includes(accountTypeName)) {
    return { accountTypeName, marginOrCash: MARGIN_TYPES.CASH }
  }
  return { accountTypeName }
}

function choiceFor<Choice extends string>(
  accountTypeName: ACCOUNT_TYPES,
  accountTypes: ACCOUNT_TYPES[]
): Choice | undefined {
  if (!accountTypes.includes(accountTypeName)) {
    return undefined
  }
  return accountTypeToChoiceMapping[accountTypeName] as Choice | undefined
}

function accountTypeSelectChoice(
  accountTypeName: ACCOUNT_TYPES
): ACCOUNT_TYPE_CHOICES {
  if (JOINT_ACCOUNT_TYPES.includes(accountTypeName)) {
    return ACCOUNT_TYPE_CHOICES.JOINT
  }
  if (RETIREMENT_ACCOUNT_TYPES.includes(accountTypeName)) {
    return ACCOUNT_TYPE_CHOICES.RETIREMENT
  }
  if (ENTITY_ACCOUNT_TYPES.includes(accountTypeName)) {
    return ACCOUNT_TYPE_CHOICES.ENTITY
  }
  return ACCOUNT_TYPE_CHOICES.INDIVIDUAL_BROKERAGE
}

export const accountTypeSelectAdapter = {
  toForm: (application, formData) => {
    formData.accountType = accountTypeSelectChoice(application.accountTypeName)
    formData.accountTypeName = application.accountTypeName
    return formData
  },
  toPatch: (data) => accountTypePatch(data.accountType),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof accountTypeSelectSchema>
  >,
  "toForm" | "toPatch"
>

export const jointAccountTypeSchema = object({
  jointAccountType: mixed<JOINT_ACCOUNT_CHOICES>()
    .oneOf(Object.values(JOINT_ACCOUNT_CHOICES))
    .required(),
})

export const jointAccountTypeAdapter = {
  toForm: (application, formData) => {
    formData.jointAccountType = choiceFor<JOINT_ACCOUNT_CHOICES>(
      application.accountTypeName,
      JOINT_ACCOUNT_TYPES
    )
    return formData
  },
  toPatch: (data) => accountTypePatch(data.jointAccountType),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof jointAccountTypeSchema>
  >,
  "toForm" | "toPatch"
>

export const retirementAccountTypeSchema = object({
  retirementAccountType: mixed<RETIREMENT_ACCOUNT_CHOICES>()
    .oneOf(Object.values(RETIREMENT_ACCOUNT_CHOICES))
    .required(),
})

export const decedentNameSchema = object({
  decedentName: string().required(),
})

export const decedentNameAdapter = {
  toForm: (application, formData) => {
    formData.decedentName = application.decedentName ?? undefined
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string("decedentName", data.decedentName)
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof decedentNameSchema>
  >,
  "toForm" | "toPatch"
>

export const iraIndividualAccountSchema = object({
  iraIndividualAccountType: mixed<IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES>()
    .oneOf(Object.values(IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES))
    .required(),
})

export const iraIndividualAccountAdapter = {
  toForm: (application, formData) => {
    formData.iraIndividualAccountType =
      choiceFor<IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES>(
        application.accountTypeName,
        IRA_INDIVIDUAL
      )
    return formData
  },
  toPatch: (data) => accountTypePatch(data.iraIndividualAccountType),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof iraIndividualAccountSchema>
  >,
  "toForm" | "toPatch"
>

export const iraBeneficiaryOrInheritedAccountSchema = object({
  iraBeneficiaryOrInheritedAccountType:
    mixed<IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES>()
      .oneOf(Object.values(IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES))
      .required(),
})

export const iraBeneficiaryOrInheritedAccountAdapter = {
  toForm: (application, formData) => {
    formData.iraBeneficiaryOrInheritedAccountType =
      choiceFor<IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES>(
        application.accountTypeName,
        BENEFICIARY_ACCOUNT_TYPES
      )
    return formData
  },
  toPatch: (data) =>
    accountTypePatch(data.iraBeneficiaryOrInheritedAccountType),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof iraBeneficiaryOrInheritedAccountSchema>
  >,
  "toForm" | "toPatch"
>

export const entityAccountTypeSchema = object({
  entityAccountType: string()
    .oneOf(Object.values(ENTITY_ACCOUNT_TYPE_CHOICES))
    .required(),
})

export const entityAccountTypeAdapter = {
  toForm: (application, formData) => {
    formData.entityAccountType = choiceFor<ENTITY_ACCOUNT_TYPE_CHOICES>(
      application.accountTypeName,
      ENTITY_ACCOUNT_TYPES
    )
    return formData
  },
  toPatch: (data) =>
    accountTypePatch(data.entityAccountType as ENTITY_ACCOUNT_TYPE_CHOICES),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof entityAccountTypeSchema>
  >,
  "toForm" | "toPatch"
>

export const taxElectionSchema = object({
  entityTaxElection: string().oneOf(TAX_ELECTION_ORDERED).required(),
})

export const accountTypeSchema = object({
  accountTypeSelect: accountTypeSelectSchema,
  jointAccountType: jointAccountTypeSchema,
  retirementAccountType: object({
    retirementAccountSelect: retirementAccountTypeSchema,
    iraIndividualAccountType: iraIndividualAccountSchema,
    iraBeneficiaryOrInheritedAccountType:
      iraBeneficiaryOrInheritedAccountSchema,
    decedentName: decedentNameSchema,
  }),
  entityAccountType: object({
    entityType: entityAccountTypeSchema,
    taxElection: taxElectionSchema,
  }),
})
